import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';

import { PriceDisplay } from 'dibs-buyer-price';
import currencies from 'dibs-currency-helpers/exports/currencies';

import { useServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';

import {
    type PriceDisplayBot_item$key,
    type PriceDisplayBot_item$data,
} from './__generated__/PriceDisplayBot_item.graphql';

import dibsCss from 'dibs-css';
import classNames from 'classnames';

const itemFragment = graphql`
    fragment PriceDisplayBot_item on Item {
        skus {
            serviceId
            displayPriceDefault: displayPrice(priceBookName: "DEFAULT") {
                convertedAmountList {
                    currency
                    amount
                }
                isVatIncluded
            }
            displayPriceAU: displayPrice(priceBookName: "AU") {
                convertedAmountList {
                    currency
                    amount
                }
                isVatIncluded
            }
            displayPriceEU: displayPrice(priceBookName: "EU") {
                convertedAmountList {
                    currency
                    amount
                }
                isVatIncluded
            }
            displayPriceGB: displayPrice(priceBookName: "GB") {
                convertedAmountList {
                    currency
                    amount
                }
                isVatIncluded
            }
        }
        ...PriceDisplay_item
            @arguments(
                userId: $userId
                isTrade: $isTrade
                priceBookName: $priceBookName
                userCountryCode: $userCountryCode
                catchNonFatalErrors: false
            )
    }
`;

type Props = {
    item: PriceDisplayBot_item$key;
    selectedSkuId: string | null;
};

type Sku = NonNullable<NonNullable<PriceDisplayBot_item$data['skus']>[number]>;

const getBotPriceOverrides = (
    sku: Sku | null,
    currency: string
): { overridePrice?: number; overrideIsVatIncluded?: boolean; overridePriceBookName?: string } => {
    let displayPrice: Sku['displayPriceDefault'] | undefined;
    let priceBook: string;

    switch (currency) {
        case 'AUD':
            displayPrice = sku?.displayPriceAU;
            priceBook = 'AU';
            break;
        case 'EUR':
            displayPrice = sku?.displayPriceEU;
            priceBook = 'EU';
            break;
        case 'GBP':
            displayPrice = sku?.displayPriceGB;
            priceBook = 'GB';
            break;
        default:
            displayPrice = sku?.displayPriceDefault;
            priceBook = 'DEFAULT';
    }

    const price = displayPrice?.[0];
    const amount = price?.convertedAmountList?.find(e => e?.currency === currency)?.amount;

    return {
        overridePrice: amount ?? undefined,
        overrideIsVatIncluded: price?.isVatIncluded ?? undefined,
        overridePriceBookName: priceBook,
    };
};

export const PriceDisplayBot: FC<Props> = ({ item: itemRef, selectedSkuId }) => {
    const item = useFragment(itemFragment, itemRef);
    const { isMobile } = useServerVarsContext();
    const selectedSku = item?.skus?.find(sku => sku?.serviceId === selectedSkuId) || null;
    const skuForPricing = selectedSku || item?.skus?.[0] || null;

    return (
        <div className={classNames(dibsCss.wFull, { [dibsCss.block]: isMobile })}>
            {currencies.map(curr => (
                <PriceDisplay
                    key={curr}
                    item={item}
                    useSvgIcon
                    displayVatLabel
                    sku={null}
                    currency={curr}
                    transaction={null}
                    {...getBotPriceOverrides(skuForPricing, curr)}
                />
            ))}
        </div>
    );
};
